import * as React from 'react'
import { ComponentProps, JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetInvoiceNoRequestParams } from '@/hooks/useApi/useFiltersApi/types'

export type InvoiceNoFilterProps = {
    name: string
    onChange: (invoiceNo: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    params: Partial<GetInvoiceNoRequestParams>
} & Pick<FilterProps, 'containerRef'>

export const InvoiceNoFilter: React.FC<InvoiceNoFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, params, onChange } = props
    const { useInvoiceNoQuery } = useFiltersApi()

    const query = useInvoiceNoQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, string>>(() => {
        if (!data) return new Map<string, string>()
        return new Map(data.map((inv) => [inv, inv]))
    }, [data])

    const options = data?.map((invoiceNo) => {
        return {
            value: invoiceNo,
            label: invoiceNo,
        }
    })

    const renderRichLabel = React.useCallback(
        (
            option: Option,
            highlightMatchingText?: (input: string) => string | JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                return (
                    <div>{highlightMatchingText?.(rawOption) ?? rawOption}</div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        disabled: !!disabled,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.invoice.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t('filters.invoice.label'),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
    }

    return <SelectFilter {...passedProps} />
}
